import type { AutomationEntityUpdateLogEntry } from '@repo/api-codegen';
import type { EntityType } from '@repo/common/enums/entityType.ts';
import type { IBaseModel } from '@repo/common/models/baseModel.ts';
import { Text } from '@repo/foundations';
import { useMemo } from 'react';
import type { AutomationJob } from '../../api/types/models/automation/automationJob';
import {
	formatAutomationAction,
	formatAutomationActivityLog,
} from '../AutomationV2/utils.ts';
import { LastUpdatedRender, TitleRender } from '../TableV2/render';
import type { ExtendedDataTableColumn } from '../TableV2/types';
import { AutomationStatusColumn } from './AutomationStatusColumn.tsx';
import { getPropagateFrom } from './utils.ts';

const joinWithAnd = (items: string[] | undefined): string => {
	if (!items || items.length === 0) return '';
	if (items.length === 1) return items[0];
	if (items.length === 2) return `${items[0]} and ${items[1]}`;

	const allButLast = items.slice(0, -1).join(', ');
	const last = items[items.length - 1];
	return `${allButLast} and ${last}`;
};

export const useColumns = (): ExtendedDataTableColumn<AutomationJob>[] =>
	useMemo(
		() => [
			{
				titleLabel: 'Run time',
				accessor: 'started_at',
				render: (record) => (
					<LastUpdatedRender record={record} field={'started_at'} />
				),
			},
			{
				titleLabel: 'Action',
				accessor: 'action',
				textAlignment: 'left',
				render: (job: AutomationJob) => (job.is_rollback ? 'Revert' : 'Run'),
			},
			{
				titleLabel: 'Resources updated',
				accessor: 'affected_records_count',
				textAlignment: 'right',
				render: (job: AutomationJob) => job.affected_records_count,
			},
			{
				titleLabel: 'Status',
				accessor: 'status',
				textAlignment: 'left',
				render: (record) => <AutomationStatusColumn job={record} />,
			},
		],
		[]
	);

export const useHistoryTableColumns = (): ExtendedDataTableColumn<
	AutomationEntityUpdateLogEntry & IBaseModel
>[] => {
	const columns: ExtendedDataTableColumn<AutomationEntityUpdateLogEntry>[] =
		useMemo(
			() => [
				{
					titleLabel: 'Resource',
					accessor: 'title',
					render: (record) => (
						<TitleRender
							record={{
								title: record.entity.title ?? undefined,
								title_cased: record.entity.title_cased ?? undefined,
								id: record.entity.id,
								entity_type: record.entity.entity_type as EntityType,
								icon: record.entity.icon ?? undefined,
							}}
						/>
					),
					width: '150px',
				},
				{
					titleLabel: 'Changes',
					accessor: 'Changes',
					render: (record: AutomationEntityUpdateLogEntry) => {
						const changes: Array<string> = [];

						for (const update of record.updates) {
							changes.push(formatAutomationAction(update.type, update.value));
						}

						if (record.propagate_updates) {
							const from = getPropagateFrom(record.propagate_updates.to);

							changes.push(
								`Propagated  ${joinWithAnd(record.propagate_updates.fields)} from ${from}`
							);
						}

						// Only check activity logs if there are no changes currently
						// being displayed. This is to make sure no duplicates changes
						// are displayed when regular changes are applied
						if (record.activity_logs && changes.length === 0) {
							for (const activity_log of record.activity_logs) {
								const formatted = formatAutomationActivityLog(
									activity_log.field,
									activity_log.value
								);
								if (formatted) changes.push(formatted);
							}
						}

						if (changes.length > 0) {
							return (
								<>
									{changes.map((c, i) => (
										<Text size={'sm'} key={record.entity.id + i}>
											{c}
										</Text>
									))}
								</>
							);
						}

						return 'No changes';
					},
				},
			],
			[]
		);

	return columns;
};
